import React, { useState, useEffect } from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import {
  Home,
  Inventory,
  LocalShipping,
  Landscape,
  MonitorHeart,
  Recycling,
} from "@mui/icons-material";

// Animation keyframes
const boxLoad = keyframes`
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.8);
  }
  70% {
    opacity: 0.8;
    transform: translateY(-5px) scale(1.02);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const floatStair = keyframes`
  0%, 100% { 
    transform: translateY(0px); 
  }
  50% { 
    transform: translateY(-5px); 
  }
`;

const slideInFromLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-30px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const gradientShift = keyframes`
  0%, 100% { 
    background-position: 0% 50%;
  }
  50% { 
    background-position: 100% 50%;
  }
`;

const pulseGlow = keyframes`
  0%, 100% { 
    box-shadow: 0 4px 15px rgba(46, 125, 50, 0.3);
  }
  50% { 
    box-shadow: 0 6px 20px rgba(46, 125, 50, 0.5);
  }
`;

// Styled Components
const HeaderContainer = styled(Box)({
  textAlign: "center",
  marginBottom: "20px",
  padding: "16px",
  background: "linear-gradient(135deg, #2d8659 0%, #000 50%, #2d8659 100%)",
  backgroundSize: "200% 200%",
  animation: `${gradientShift} 3s ease infinite, ${slideInFromLeft} 0.6s ease-out`,
  //   borderRadius: "12px",
  backdropFilter: "blur(8px)",
  color: "white",
  boxShadow: "0 6px 20px rgba(46, 125, 50, 0.3)",
  position: "relative",
  overflow: "hidden",
  borderRadius: "60px",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)",
    animation: `${slideInFromLeft} 10s ease-in-out infinite`,
  },
});

const MainTitle = styled(Typography)({
  fontSize: "2.5rem",
  fontWeight: 700,
  marginBottom: "3px",
  textShadow: "1px 1px 4px rgba(0,0,0,0.2)",
  position: "relative",
  zIndex: 2,
});

const ProcessGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gridTemplateRows: "repeat(2, auto)",
  gap: "60px 20px",
  height: "calc(100% - 100px)",
  padding: "20px",
  position: "relative",
});

const ProcessStep = styled(Card)(({ hovered, position, loaded, boxLoaded }) => {
  const getGridPosition = () => {
    const positions = {
      1: "1 / 1", // Top-left
      2: "1 / 2", // Top-middle
      3: "1 / 3", // Top-right
      4: "2 / 3", // Bottom-right
      5: "2 / 2", // Bottom-middle
      6: "2 / 1", // Bottom-left
    };
    return positions[position];
  };

  return {
    gridArea: getGridPosition(),
    border: `2px solid ${hovered ? "#66bb6a" : "#e8f5e9"}`,
    background: "linear-gradient(135deg, #2d8659 0%, #000 50%, #2d8659 100%)",
    backdropFilter: "blur(8px)",
    transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
    animation: boxLoaded
      ? `${boxLoad} 1.2s ease-out both, ${floatStair} 4s ease-in-out infinite`
      : "none",
    animationDelay: boxLoaded ? `0s, ${(position - 1) * 2}s` : "none",
    cursor: "pointer",
    position: "relative",
    overflow: "visible",
    borderRadius: "16px",
    minHeight: "140px",
    opacity: boxLoaded ? 1 : 0,
    // transform: boxLoaded
    //   ? "translateY(0) scale(1)"
    //   : "translateY(30px) scale(0.8)",
    boxShadow: hovered
      ? `0 12px 30px rgba(102, 187, 106, 0.25), 0 0 0 1px rgba(102, 187, 106, 0.1)`
      : `0 6px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(46, 125, 50, 0.1)`,
    transform:
      hovered && boxLoaded
        ? "translateY(-6px) scale(1.02)"
        : boxLoaded
        ? "translateY(0) scale(1)"
        : "translateY(30px) scale(0.8)",

    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "4px",
      background: "linear-gradient(90deg, #2e7d32, #66bb6a, #2e7d32)",
      transform: hovered ? "scaleX(1)" : "scaleX(0)",
      transformOrigin: "left",
      transition: "transform 0.3s ease",
      borderRadius: "16px 16px 0 0",
    },
  };
});

const StepNumber = styled(Box)(({ hovered, boxLoaded }) => ({
  position: "absolute",
  top: "10%",
  right: "4%",
  background: "linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)",
  color: "white",
  width: "50px",
  height: "50px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: "1.6rem",
  boxShadow: "0 4px 12px rgba(46, 125, 50, 0.4)",
  transition: "all 0.2s ease",
  transform:
    hovered && boxLoaded ? "scale(1.2) rotate(10deg)" : "scale(1) rotate(0deg)",
  zIndex: 10,
  opacity: boxLoaded ? 1 : 0,
}));

const IconContainer = styled(Box)(({ hovered, boxLoaded }) => ({
  color: "white",
  background: "linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "50px",
  height: "50px",
  borderRadius: "12px",
  flexShrink: 0,
  fontSize: "2.5rem",
  boxShadow: "0 4px 15px rgba(46, 125, 50, 0.3)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  transform:
    hovered && boxLoaded ? "scale(1.1) rotate(5deg)" : "scale(1) rotate(0deg)",
  animation: boxLoaded ? `${floatStair} 6s ease-in-out infinite` : "none",
  opacity: boxLoaded ? 1 : 0,
  "&:hover": {
    animation: boxLoaded ? `${pulseGlow} 1.2s ease-in-out infinite` : "none",
  },
}));

const FloatingParticle = styled(Box)(({ index, boxLoaded }) => ({
  position: "absolute",
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  background: "linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)",
  animation: boxLoaded ? `${floatStair} 5s ease-in-out infinite` : "none",
  animationDelay: boxLoaded ? `${index * 0.8}s` : "0s",
  opacity: boxLoaded ? 0.3 : 0,
}));

const SuthraPunjabProcess = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [loadedStates, setLoadedStates] = useState({
    box1: false,
    box2: false,
    box3: false,
    box4: false,
    box5: false,
    box6: false,
    arrow1: false,
    arrow2: false,
    arrow3: false,
    arrow4: false,
    arrow5: false,
  });

  useEffect(() => {
    const loadSequence = async () => {
      // Box 1
      await new Promise((resolve) => setTimeout(resolve, 300));
      setLoadedStates((prev) => ({ ...prev, box1: true }));

      // Arrow 1 (from Box 1 to Box 2)
      await new Promise((resolve) => setTimeout(resolve, 600));
      setLoadedStates((prev) => ({ ...prev, arrow1: true }));

      // Box 2
      await new Promise((resolve) => setTimeout(resolve, 300));
      setLoadedStates((prev) => ({ ...prev, box2: true }));

      // Arrow 2 (from Box 2 to Box 3)
      await new Promise((resolve) => setTimeout(resolve, 600));
      setLoadedStates((prev) => ({ ...prev, arrow2: true }));

      // Box 3
      await new Promise((resolve) => setTimeout(resolve, 300));
      setLoadedStates((prev) => ({ ...prev, box3: true }));

      // Arrow 3 (from Box 3 down)
      await new Promise((resolve) => setTimeout(resolve, 600));
      setLoadedStates((prev) => ({ ...prev, arrow3: true }));

      // Box 4
      await new Promise((resolve) => setTimeout(resolve, 300));
      setLoadedStates((prev) => ({ ...prev, box4: true }));

      // Arrow 4 (from Box 4 to Box 5)
      await new Promise((resolve) => setTimeout(resolve, 600));
      setLoadedStates((prev) => ({ ...prev, arrow4: true }));

      // Box 5
      await new Promise((resolve) => setTimeout(resolve, 300));
      setLoadedStates((prev) => ({ ...prev, box5: true }));

      // Arrow 5 (from Box 5 to Box 6)
      await new Promise((resolve) => setTimeout(resolve, 600));
      setLoadedStates((prev) => ({ ...prev, arrow5: true }));

      // Box 6
      await new Promise((resolve) => setTimeout(resolve, 300));
      setLoadedStates((prev) => ({ ...prev, box6: true }));
    };

    loadSequence();
  }, []);

  const processes = [
    {
      id: 1,
      title: "Door to Door Waste Collection",
      description:
        "Manual & Mechanical Sweeping, Washing, Desilting operations across Punjab households.",
      icon: Home,
      particles: [
        { top: "10%", left: "5%" },
        { top: "80%", left: "8%" },
        { top: "40%", right: "5%" },
      ],
      position: 1,
      connector: "right",
      boxLoaded: loadedStates.box1,
      arrowLoaded: loadedStates.arrow1,
    },
    {
      id: 2,
      title: "Container Based Collection",
      description:
        "Scheduled Waste Pickup, Digitally Monitored & Routes Optimized.",
      icon: Inventory,
      particles: [
        { top: "15%", left: "6%" },
        { top: "75%", right: "6%" },
      ],
      position: 2,
      connector: "right",
      boxLoaded: loadedStates.box2,
      arrowLoaded: loadedStates.arrow2,
    },
    {
      id: 3,
      title: "Temporary Collection Points",
      description: "(TCPs) & Bulk Waste Transportation Secondary Collection.",
      icon: LocalShipping,
      particles: [
        { top: "20%", right: "8%" },
        { top: "85%", left: "5%" },
      ],
      position: 3,
      connector: "down",
      boxLoaded: loadedStates.box3,
      arrowLoaded: loadedStates.arrow3,
    },
    {
      id: 6,
      title: "Waste to Value",
      description:
        "Processing Waste to Electricity, Biogas, Compost, RDF resources.",
      icon: Recycling,
      particles: [
        { top: "35%", left: "8%" },
        { top: "65%", right: "5%" },
      ],
      position: 6,
      connector: null,
      boxLoaded: loadedStates.box6,
      arrowLoaded: false,
    },
    {
      id: 5,
      title: "AI Based Digital Monitoring",
      description:
        "Citizen Feedback Model - Billing to Consumers with AI systems.",
      icon: MonitorHeart,
      particles: [
        { top: "30%", right: "6%" },
        { top: "60%", left: "7%" },
      ],
      position: 5,
      connector: "left",
      boxLoaded: loadedStates.box5,
      arrowLoaded: loadedStates.arrow5,
    },

    {
      id: 4,
      title: "Managed Landfills",
      description:
        "Efficient waste disposal at designated sites with environmental controls.",
      icon: Landscape,
      particles: [
        { top: "25%", left: "6%" },
        { top: "70%", right: "7%" },
      ],
      position: 4,
      connector: "left",
      boxLoaded: loadedStates.box4,
      arrowLoaded: loadedStates.arrow4,
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        background:
          "linear-gradient(140deg, #e6e9c9ff 0%, #585a41ff 30%, #e6e9c9ff 80%)",
        backgroundSize: "200% 200%",
        position: "relative",
        borderRadius: "50px",
        overflow: "hidden",
        padding: "15px",
      }}
    >
      <HeaderContainer>
        <MainTitle>⚙️ Suthra Punjab Process ⚙️</MainTitle>
      </HeaderContainer>

      <ProcessGrid>
        {processes.map((process) => {
          const Icon = process.icon;
          const isHovered = hoveredIndex === process.position;

          return (
            <Box key={process.id} sx={{ position: "relative" }}>
              {/* Connector Arrows */}

              <ProcessStep
                hovered={isHovered}
                position={process.position}
                loaded={true}
                boxLoaded={process.boxLoaded}
                onMouseEnter={() => setHoveredIndex(process.position)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <StepNumber hovered={isHovered} boxLoaded={process.boxLoaded}>
                  {process.position}
                </StepNumber>

                {/* Floating particles */}
                {process.particles.map((particle, particleIndex) => (
                  <FloatingParticle
                    key={particleIndex}
                    index={particleIndex}
                    boxLoaded={process.boxLoaded}
                    sx={{
                      top: particle.top,
                      left: particle.left,
                      right: particle.right,
                    }}
                  />
                ))}

                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    gap: 2,
                    padding: "16px",
                    position: "relative",
                    zIndex: 2,
                    height: "100%",
                    justifyContent: "center",
                  }}
                >
                  <IconContainer
                    hovered={isHovered}
                    boxLoaded={process.boxLoaded}
                  >
                    <Icon fontSize="inherit" />
                  </IconContainer>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 900,
                        mb: 1,
                        fontSize: "1.5rem",
                        lineHeight: 1.3,
                        transition: "all 0.6s ease",
                        background:
                          "linear-gradient(135deg, #42b147ff 0%, #66bb6a 100%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        transform: isHovered
                          ? "translateY(-2px)"
                          : "translateY(0)",
                        opacity: process.boxLoaded ? 1 : 0,
                        transitionDelay: process.boxLoaded ? "0.3s" : "0s",
                      }}
                    >
                      {process.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        lineHeight: 1.4,
                        fontSize: "1.3rem",
                        transition: "all 0.6s ease",
                        color: isHovered ? "#2e7d32" : "#ffffffff",
                        transform: isHovered
                          ? "translateY(-1px)"
                          : "translateY(0)",
                        opacity: process.boxLoaded ? 1 : 0,
                        transitionDelay: process.boxLoaded ? "0.5s" : "0s",
                      }}
                    >
                      {process.description}
                    </Typography>
                  </Box>
                </CardContent>
              </ProcessStep>
            </Box>
          );
        })}
      </ProcessGrid>
    </Box>
  );
};

export default SuthraPunjabProcess;
